import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Facebook, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { signup } from "@/lib/api";

export default function SignupForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", volunteer: false, consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please check the consent box so we can contact you.");
      return;
    }
    setLoading(true);
    try {
      const r = await signup({ ...form, phone: form.phone || "" });
      setDone(true);
      toast.success(r.data.message || "You're on the list!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="signup" className="relative bg-navy noise py-24 md:py-36 px-5 md:px-10 overflow-hidden" data-testid="signup-section">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 md:gap-20 items-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-gold mb-6">Join The Movement</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gold leading-[1.02]">
            Stand with
            <span className="block italic font-medium">Dave!</span>
          </h2>
          <p className="text-white/90 text-base md:text-lg leading-relaxed mt-8 max-w-md">
            Sign up for campaign updates — or tell us you want to volunteer. Either way, you&rsquo;ll be first to
            know about events, voter info, and how to help us win on November 3.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="bg-[#F8F9FA] rounded-sm p-7 md:p-10 shadow-[0_48px_96px_-32px_rgba(0,0,0,0.5)]"
        >
          {done ? (
            <div className="text-center py-10" data-testid="signup-success">
              <CheckCircle2 className="w-14 h-14 text-electric mx-auto mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-navy mb-3">You&rsquo;re in.</h3>
              <p className="text-navy/70 text-sm md:text-base leading-relaxed max-w-sm mx-auto">
                Thank you for standing with Dave. Watch your inbox for a confirmation — and we&rsquo;ll see you
                on the trail.
              </p>
              <a
                href="https://www.facebook.com/DaveWilliamsforMHA/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 mt-8 bg-[#1877F2] hover:bg-[#1466d8] text-white text-xs uppercase tracking-[0.2em] font-bold px-6 py-3.5 rounded-full transition-colors"
                data-testid="signup-facebook-link"
              >
                <Facebook className="w-4 h-4" /> Follow us on Facebook
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
              <div className="space-y-2">
                <Label htmlFor="signup-name" className="text-xs uppercase tracking-[0.2em] font-semibold text-navy/70">
                  Full Name *
                </Label>
                <Input
                  id="signup-name"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Your name"
                  className="h-12 text-base rounded-sm border-navy/20 focus-visible:ring-electric"
                  data-testid="signup-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-email" className="text-xs uppercase tracking-[0.2em] font-semibold text-navy/70">
                  Email *
                </Label>
                <Input
                  id="signup-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@example.com"
                  className="h-12 text-base rounded-sm border-navy/20 focus-visible:ring-electric"
                  data-testid="signup-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-phone" className="text-xs uppercase tracking-[0.2em] font-semibold text-navy/70">
                  Phone (optional)
                </Label>
                <Input
                  id="signup-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="(701) 555-0123"
                  className="h-12 text-base rounded-sm border-navy/20 focus-visible:ring-electric"
                  data-testid="signup-phone-input"
                />
              </div>

              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="signup-volunteer"
                  checked={form.volunteer}
                  onCheckedChange={(v) => set("volunteer", !!v)}
                  className="mt-0.5 border-navy/30 data-[state=checked]:bg-electric data-[state=checked]:border-electric"
                  data-testid="signup-volunteer-checkbox"
                />
                <Label htmlFor="signup-volunteer" className="text-sm text-navy/80 leading-snug cursor-pointer">
                  I want to <strong>volunteer</strong> and help the campaign.
                </Label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="signup-consent"
                  required
                  checked={form.consent}
                  onCheckedChange={(v) => set("consent", !!v)}
                  className="mt-0.5 border-navy/30 data-[state=checked]:bg-electric data-[state=checked]:border-electric"
                  data-testid="signup-consent-checkbox"
                />
                <Label htmlFor="signup-consent" className="text-xs text-navy/60 leading-relaxed cursor-pointer">
                  I agree to receive campaign updates by email and text. My information is used only for
                  campaign outreach and is never sold. *
                </Label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-navy hover:bg-navy-700 disabled:opacity-60 text-white text-sm uppercase tracking-[0.2em] font-bold py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(31,78,121,0.5)] focus:outline-none focus:ring-2 focus:ring-navy/50 flex items-center justify-center gap-2"
                data-testid="signup-submit-btn"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Signing up…" : "Count Me In"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
